import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mail, Trash2, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function AdminMessages() {
  const { apiFetch } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await apiFetch("GET", "/api/admin/contact-messages");
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiFetch("DELETE", `/api/admin/contact-messages/${confirmDelete.id}`);
      toast.success("Message deleted");
      setConfirmDelete(null);
      await load(true);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-messages-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="mono-label text-primary text-[10px]">Inbox</span>
          <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "var(--font-display)" }}>
            Contact Messages
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Submissions from the public contact form. Replies should be sent from your email client.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="admin-messages-refresh"
        >
          {refreshing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Refresh
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center min-h-[30vh]">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : messages.length === 0 ? (
        <Card className="p-10 bg-card border-border text-center">
          <Mail className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No messages yet.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {messages.map((m, i) => (
            <Card key={m.id || i} className="p-5 bg-card border-border" data-testid={`admin-message-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{m.name}</h3>
                    <Badge variant="outline" className="text-[10px] text-primary border-primary/30">
                      <a
                        href={`mailto:${m.email}`}
                        className="hover:underline"
                        data-testid={`admin-message-${i}-email-link`}
                      >
                        {m.email}
                      </a>
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{formatDate(m.timestamp)}</p>
                  <p className="text-sm text-foreground/90 leading-relaxed mt-3 whitespace-pre-wrap">
                    {m.message}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-rose-400 hover:text-rose-300 flex-shrink-0"
                  onClick={() => setConfirmDelete(m)}
                  aria-label="Delete message"
                  data-testid={`admin-message-${i}-delete`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={Boolean(confirmDelete)} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the message from <strong>{confirmDelete?.name}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="admin-messages-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
